import { isEqual, noop } from "lodash";
import _ from "lodash";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";

import type { CostCenters } from "@/apis/gql/types";
import { CostCenterDropdownInput } from "@/components/shared/form/cost-center-dropdown-input";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";

const formSchema = z.object({
  costCenterId: z.string().min(1).max(255).nullable(),
  lineItemIds: z.array(z.string().min(1).max(255)),
});

export type FormValues = z.infer<typeof formSchema>;

type Props = {
  readOnly?: boolean | string | React.ReactNode;
  disabled?: boolean | string | React.ReactNode;
  fullWidth?: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  initialValues: {
    costCenterId: string | null;
    lineItemIds: string[];
  };
  costCenters: CostCenters[];
  onSubmit: (value: FormValues) => Promise<void> | void;
  onError?: (error: Error) => void;
  onSuccess?: (value: string | null) => void;
  variant?: "outline" | "ghost";
};

export const CostCenterForm = ({
  variant,
  costCenters,
  className,
  initialValues,
  onError = noop,
  onSubmit = noop,
  onSuccess = noop,
  readOnly = false,
  disabled = false,
  fullWidth = false,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialValues,
  });

  const { reset } = form;

  useEffect(() => {
    if (!isEqual(form.getValues().costCenterId, initialValues.costCenterId)) {
      form.reset(initialValues);
    }
  }, [initialValues.costCenterId]);

  const handleSubmit = async ({ lineItemIds, costCenterId }: FormValues) => {
    const value = costCenterId !== "unknown" ? costCenterId : null;

    const toastId = toast.loading(t("generic_loading"));

    try {
      await onSubmit({ lineItemIds, costCenterId: value });

      await onSuccess(value);

      setTimeout(() => {
        toast.dismiss(toastId);
      }, 100);
    } catch (_error) {
      const error = _error as Error;
      onError(error);
      toast.error(t("generic_error"), { richColors: true, id: toastId });
      reset(initialValues, { keepIsValid: true });
    }
  };

  return (
    <Form {...form}>
      <form>
        <CostCenterDropdownInput
          name="costCenterId"
          options={costCenters}
          fullWidth={fullWidth}
          className={className}
          readOnly={readOnly}
          variant={variant}
          placeholder={t("cost_centers.select_cost_center")}
          disabled={disabled}
        />
        <AutoSaveForm onSubmit={handleSubmit} debounceTime={0} />
      </form>
    </Form>
  );
};
